import {
  Button,
  ButtonGroup,
  Content,
  Dialog,
  Divider,
  Flex,
  Footer,
  Form,
  Heading,
  ProgressCircle,
  Text,
} from '@adobe/react-spectrum';
import FieldLabel from '@react/react-spectrum/FieldLabel';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import Provider from '@react/react-spectrum/Provider';
import Textarea from '@react/react-spectrum/Textarea';
import Textfield from '@react/react-spectrum/Textfield';
import Tooltip from '@react/react-spectrum/Tooltip';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { useVisuallyHidden } from 'react-aria';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { excApi, handleApiCall } from '@exchange-frontends/api';
import { useIsMounted } from '@exchange-frontends/custom-hooks';

import { useStore } from '../../../store';
import { UserDataContext } from '../../App';
import { Label, RedText } from './RatingsAndReviews';
import StarRatingGroup from './StarRatingGroup';

const TextArea = styled(Textarea)`
  &&& {
    min-height: 75px;
    width: 100%;
  }
`;

const TextField = styled(Textfield)`
  &&& {
    width: 100%;
  }
`;

const TITLE_MAX_CHARS = 100;
const REVIEW_TEXT_MAX_CHARS = 2000;

const validateTitle = (newVal) => (newVal?.length >= 3 && newVal?.length <= TITLE_MAX_CHARS ? 'valid' : 'invalid');
const validateReviewText = (newVal) =>
  newVal?.length >= 3 && newVal?.length <= REVIEW_TEXT_MAX_CHARS ? 'valid' : 'invalid';

export const LeaveReview = (props) => {
  const { close, variant, readOnly, onSuccessfulReview, fixedValue, label } = props;

  const isMounted = useIsMounted();
  const { profile, accessToken } = useContext(UserDataContext) ?? {};
  const { id: assetId } = useParams();
  const assetVersion = useStore((state) => state.assetVersion);
  const currentUserReview = useStore((state) => state.currentUserReview);
  const { visuallyHiddenProps } = useVisuallyHidden();

  const [title, setTitle] = useState({
    value: currentUserReview?.title ?? '',
    valid: currentUserReview?.title ? 'valid' : null,
  });
  const [reviewText, setReviewText] = useState({
    value: currentUserReview?.text ?? '',
    valid: currentUserReview?.text ? 'valid' : null,
  });
  const [rating, setRating] = useState(currentUserReview?.rating ?? 0);
  const [errored, setErrored] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [shouldShowRatingTooltip, setShouldShowRatingTooltip] = useState(false);
  const tooltipType = errored ? 'error' : 'info';
  const tooltipText = errored ? errorMessage ?? 'Error! Please try again.' : 'Rating saved!';

  const onSubmit = async (inputRating) => {
    setErrored(false);
    setLoading(true);
    if (accessToken) {
      try {
        const commonParams = {
          accessToken,
          rating: variant === 'review' ? rating : inputRating,
          assetVersion,
          imsUserId: profile.userId,
          title: variant === 'review' ? title.value.trim() : currentUserReview?.title,
          text: variant === 'review' ? reviewText.value.trim() : currentUserReview?.text,
          authorName: profile.first_name + ' ' + profile.last_name,
        };
        if (currentUserReview) {
          const handler = await handleApiCall(excApi.editReview, { ...commonParams, reviewId: currentUserReview.id });
          const updatedReview = await handler.run();
          useStore.setState({ currentUserReview: updatedReview });
        } else {
          const handler = await handleApiCall(excApi.addReview, {
            ...commonParams,
            assetId,
            authorGuid: profile.userId,
          });
          const newReview = await handler.run();
          useStore.setState({ currentUserReview: newReview });
        }
        if (variant === 'review') {
          onSuccessfulReview();
          close();
        }
      } catch (err) {
        console.error(err);
        setErrorMessage(err?.originalContent?.title);
        setErrored(true);
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
        if (variant === 'rating') {
          setShouldShowRatingTooltip(true);
          setTimeout(() => {
            setShouldShowRatingTooltip(false);
          }, 3000);
        }
      }
    }
  };

  const isSaveButtonDisabled =
    loading ||
    !rating ||
    reviewText.valid !== 'valid' ||
    title.valid !== 'valid' ||
    (title.value === currentUserReview?.title &&
      reviewText.value === currentUserReview?.text &&
      rating === currentUserReview?.rating);

  if (variant === 'review') {
    return (
      <Dialog size="M">
        <Heading>Leave a review</Heading>
        <Divider />
        <ButtonGroup>
          <Button variant="secondary" onPress={close} isDisabled={loading}>
            Cancel
          </Button>
          <Button autoFocus variant="cta" isDisabled={isSaveButtonDisabled} onPress={onSubmit}>
            {loading && (
              <ProgressCircle size="S" isIndeterminate marginEnd="size-100" aria-label="Saving your review" />
            )}
            <Text> Save</Text>
          </Button>
        </ButtonGroup>
        <Content>
          <Provider>
            <Form>
              <Flex alignItems="center" gap="size-300" marginBottom="size-200" width="100%">
                <StarRatingGroup onRatingSet={setRating} defaultValue={rating} label={label} />
              </Flex>
              <Flex direction="column" width="100%" marginBottom="size-100">
                <Flex justifyContent="space-between" alignItems="center">
                  <FieldLabel necessity="required" labelFor="title" id="title-label" label="Title" />{' '}
                  <Label
                    role="status"
                    aria-live="polite"
                    aria-label={`${TITLE_MAX_CHARS - title?.value?.length} characters remaining`}
                  >
                    <span aria-hidden="true">{TITLE_MAX_CHARS - title?.value?.length}</span>
                  </Label>
                </Flex>
                <TextField
                  id="title"
                  value={title.value}
                  onChange={(value) => {
                    const valid = validateTitle(value.trim());
                    setTitle({ value, valid });
                  }}
                  width="100%"
                />
              </Flex>
              <Flex direction="column" width="100%">
                <Flex justifyContent="space-between" alignItems="center">
                  <FieldLabel
                    necessity="required"
                    labelFor="review-textarea"
                    id="review-textarea-label"
                    label="Review"
                  />
                  <Label
                    role="status"
                    aria-live="polite"
                    aria-label={`${REVIEW_TEXT_MAX_CHARS - reviewText?.value?.length} characters remaining`}
                  >
                    <span aria-hidden="true">{REVIEW_TEXT_MAX_CHARS - reviewText?.value?.length}</span>
                  </Label>
                </Flex>
                <TextArea
                  id="review-textarea"
                  value={reviewText.value}
                  onChange={(value) => {
                    const valid = validateReviewText(value.trim());
                    setReviewText({ value, valid });
                  }}
                  width="100%"
                />
              </Flex>
            </Form>
          </Provider>
        </Content>
        {errored && (
          <Footer>
            <RedText>{errorMessage ?? 'Failed to save review'}</RedText>
          </Footer>
        )}
      </Dialog>
    );
  } else if (variant === 'rating') {
    return (
      <>
        <div {...visuallyHiddenProps} aria-live="polite">
          {loading ? 'Saving your rating' : shouldShowRatingTooltip ? tooltipText : ''}
        </div>
        {loading ? (
          <ProgressCircle
            aria-label={'Saving your rating'}
            size="S"
            isIndeterminate
            margin="size-100"
            data-testid="rating-loading"
          />
        ) : (
          <OverlayTrigger trigger={null} show={shouldShowRatingTooltip} placement="right">
            <StarRatingGroup
              id="your-rating-id"
              onRatingSet={onSubmit}
              isDisabled={loading}
              isReadOnly={readOnly}
              label={label}
              ratingFirst
              value={String(fixedValue ?? currentUserReview?.rating ?? '')}
            />
            <Tooltip variant={tooltipType}>{tooltipText}</Tooltip>
          </OverlayTrigger>
        )}
      </>
    );
  } else return null;
};

LeaveReview.defaultProps = {
  variant: 'review',
};

LeaveReview.propTypes = {
  readOnly: PropTypes.bool,
  /** Function to close the dialog, if variant = review */
  close: PropTypes.func,
  /** Returns just the rating stars when variant = rating. Returns a dialog with the leave a review form  when variant = review.  */
  variant: PropTypes.oneOf(['review', 'rating']).isRequired,
  /** Function tocall if the review was saved successfully, if variant = review */
  onSuccessfulReview: PropTypes.func,
  /** If set, the rating stars will be fixed to this value (used in rating variant for displaying overall rating) */
  fixedValue: PropTypes.number,
  /** Label to display above the rating stars or review form */
  label: PropTypes.string,
};
